import React, { useState } from 'react';
import { Box, Checkbox, IconButton, Typography } from '@mui/joy';
import { CaretRight as ChevronRight, CaretDown as ExpandMore } from '@phosphor-icons/react';

export interface PageOption {
  slug: string;
  title: string;
  checked: boolean;
  children?: PageOption[];
  parent?: string;
  order?: number;
}

interface PageTreeItemProps {
  page: PageOption;
  depth?: number;
  onChange: (slug: string, checked: boolean) => void;
  onToggleAll?: (slug: string, checked: boolean) => void;
}

export const PageTreeItem: React.FC<PageTreeItemProps> = ({ 
  page, 
  depth = 0, 
  onChange,
  onToggleAll 
}) => {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = page.children && page.children.length > 0;

  const handleToggle = () => {
    onChange(page.slug, !page.checked);
    if (hasChildren && onToggleAll) {
      // Toggle all children when parent is toggled
      onToggleAll(page.slug, !page.checked);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: depth * 3,
          py: 0.5,
          '&:hover': {
            backgroundColor: 'background.level1',
          },
        }}
      >
        {hasChildren ? (
          <IconButton
            size="sm"
            variant="plain"
            onClick={() => setExpanded(!expanded)}
            sx={{ mr: 0.5 }}
          >
            {expanded ? <ExpandMore /> : <ChevronRight />}
          </IconButton>
        ) : (
          <Box sx={{ width: 32 }} />
        )}
        
        <Checkbox
          checked={page.checked}
          onChange={handleToggle}
          sx={{ mr: 1 }}
        />
        
        <Typography
          level="body-md"
          sx={{
            fontWeight: hasChildren ? 'bold' : 'normal',
            color: page.checked ? 'text.primary' : 'text.secondary',
          }}
        >
          {page.title}
        </Typography>
      </Box>

      {hasChildren && expanded && (
        <Box>
          {page.children!.map((child) => (
            <PageTreeItem
              key={child.slug}
              page={child}
              depth={depth + 1}
              onChange={onChange}
              onToggleAll={onToggleAll}
            />
          ))}
        </Box>
      )}
    </>
  );
};

export default PageTreeItem;